import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { SparklesIcon } from '@heroicons/react/24/outline';

const HomePurchaseLoanCTABannerPage = () => {
    const [settings, setSettings] = useState({
        title: 'Ready to Start Building Your Dream Home?',
        description: 'Take the first step towards constructing your perfect home with Aham\'s hassle-free loan process',
        button_text: 'Talk to Our Loan Expert',
        button_link: 'https://aham.mbwhost.in/contact'
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const LOAN_TYPE = 'home_purchase_loan';

    // Fetch data
    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/admin/cta-banner/settings/${LOAN_TYPE}`);
            if (response.data.data) {
                setSettings(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load CTA banner settings');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Handle settings update
    const handleSave = async () => {
        try {
            setSaving(true);
            await axios.put(`/api/admin/cta-banner/settings/${LOAN_TYPE}`, settings);
            toast.success('CTA banner settings updated successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to update settings');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-slate-800">CTA Banner</h1>
                <p className="text-slate-500 mt-1">Manage the call-to-action banner for Home Purchase Loan</p>
            </div>

            {/* Settings Form */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
                <h2 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                    <SparklesIcon className="h-5 w-5 text-primary-600" />
                    CTA Banner Settings
                </h2>

                {loading ? (
                    <div className="space-y-4">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="h-12 bg-slate-100 rounded-xl animate-pulse"></div>
                        ))}
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Title */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Title
                            </label>
                            <input
                                type="text"
                                value={settings.title}
                                onChange={(e) => setSettings({ ...settings, title: e.target.value })}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder="e.g., Ready to Start Building Your Dream Home?"
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Description
                            </label>
                            <textarea
                                rows="3"
                                value={settings.description}
                                onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder="Enter description..."
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Button Text */}
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    Button Text
                                </label>
                                <input
                                    type="text"
                                    value={settings.button_text}
                                    onChange={(e) => setSettings({ ...settings, button_text: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    placeholder="e.g., Talk to Our Loan Expert"
                                />
                            </div>

                            {/* Button Link */}
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    Button Link
                                </label>
                                <input
                                    type="text"
                                    value={settings.button_link}
                                    onChange={(e) => setSettings({ ...settings, button_link: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    placeholder="e.g., /contact or https://..."
                                />
                            </div>
                        </div>

                        {/* Save Button */}
                        <div className="flex justify-end pt-4">
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-lg"
                            >
                                {saving ? 'Saving...' : 'Save Settings'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default HomePurchaseLoanCTABannerPage;
